import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2, Search, UserRound, Eye, EyeOff, FileDown, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtDate, fullName } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/site/StatusBadge";
import { APFormDialog } from "@/components/admin/APFormDialog";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { ImportDialog } from "@/components/admin/ImportDialog";

const Row = ({ a, onEdit, onDelete, onToggle }) => (
  <TableRow data-testid={`ap-row-${a.cnap}`}>
    <TableCell>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-emerald-50 overflow-hidden grid place-items-center shrink-0">
          {a.photo ? <img src={fileUrl(a.photo)} alt="" className="h-full w-full object-cover" /> : <UserRound className="h-5 w-5 text-emerald-700/50" />}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate">{fullName(a)}</div>
          <div className="text-xs text-slate-500 truncate">{[a.lieu_exercice, a.ville, a.province].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
    </TableCell>
    <TableCell className="font-mono font-bold text-emerald-700">{a.cnap}</TableCell>
    <TableCell className="text-sm whitespace-nowrap">{fmtDate(a.date_delivrance)}</TableCell>
    <TableCell className="text-sm whitespace-nowrap">{fmtDate(a.date_expiration)}</TableCell>
    <TableCell>
      <div className="flex flex-col items-start gap-1">
        <StatusBadge statut={a.statut} testId={`ap-status-${a.cnap}`} />
        {a.masque && <span data-testid={`ap-masque-badge-${a.cnap}`} className="text-[10px] font-bold text-rose-700 border border-rose-300 bg-rose-50 rounded-full px-2 py-0.5 whitespace-nowrap">Masqué · cotisation</span>}
      </div>
    </TableCell>
    <TableCell className="text-right whitespace-nowrap">
      <Button size="icon" variant="ghost" title={a.masque ? "Réafficher (en ordre)" : "Masquer (pas en ordre de cotisation)"} onClick={() => onToggle(a)} data-testid={`ap-toggle-masque-${a.cnap}`} className={a.masque ? "text-rose-600" : ""}>{a.masque ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
      <Button size="icon" variant="ghost" onClick={() => onEdit(a)} data-testid={`ap-edit-${a.cnap}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" onClick={() => onDelete(a)} data-testid={`ap-delete-${a.cnap}`} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></Button>
    </TableCell>
  </TableRow>
);

export default function APList() {
  const [params, setParams] = useSearchParams();
  const statut = params.get("statut") || "all";
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState({ open: false, ap: null });
  const [del, setDel] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/aps", { params: { q, statut: statut === "all" ? "" : statut } }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [q, statut]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const [exporting, setExporting] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const r = await api.get("/admin/aps/export", { params: { format, q, statut: statut === "all" ? "" : statut }, responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `registre_ap.${format}`;
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Échec de l'export"); }
    setExporting("");
  };

  const toggle = async (a) => {
    try {
      await api.patch(`/admin/aps/${a.id}/masque`, { masque: !a.masque });
      toast.success(a.masque ? "AP réaffiché" : "AP masqué (pas en ordre de cotisation)");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/aps/${del.id}`); toast.success("AP supprimé"); load(); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Assistants en pharmacie</h1>
          <p className="text-slate-500 mt-1"><span data-testid="ap-count">{items.length}</span> résultat(s)</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => exportFile("pdf")} disabled={!!exporting} data-testid="ap-export-pdf" className="rounded-full bg-white">{exporting === "pdf" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1 text-rose-600" />} PDF</Button>
          <Button variant="outline" onClick={() => exportFile("xlsx")} disabled={!!exporting} data-testid="ap-export-excel" className="rounded-full bg-white">{exporting === "xlsx" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1 text-emerald-700" />} Excel</Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="ap-import-button" className="rounded-full bg-white"><Upload className="h-4 w-4 mr-1 text-emerald-700" /> Importer Excel</Button>
          <Button onClick={() => setEdit({ open: true, ap: null })} data-testid="ap-add-button" className="rounded-full"><Plus className="h-4 w-4 mr-1" /> Nouvel AP</Button>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="ap-search-input" placeholder="Nom, CNAP, ville, lieu d'exercice…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" />
        </div>
        <Select value={statut} onValueChange={(v) => setParams(v === "all" ? {} : { statut: v })}>
          <SelectTrigger data-testid="ap-status-filter" className="sm:w-56 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="valide">Valide</SelectItem>
            <SelectItem value="expire_bientot">Expire bientôt</SelectItem>
            <SelectItem value="expiree">Expirée</SelectItem>
            <SelectItem value="sans_carte">Sans carte verte</SelectItem>
            <SelectItem value="masque">Masqués (cotisation)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-2xl border border-emerald-900/10 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Identité</TableHead><TableHead>N° CNAP</TableHead><TableHead>Délivrance</TableHead><TableHead>Expiration</TableHead><TableHead>Statut</TableHead><TableHead /></TableRow>
          </TableHeader>
          <TableBody>
            {items.map((a) => <Row key={a.id} a={a} onEdit={(ap) => setEdit({ open: true, ap })} onDelete={setDel} onToggle={toggle} />)}
            {!items.length && <TableRow><TableCell colSpan={6} data-testid="ap-empty" className="text-center text-slate-500 py-12">Aucun AP trouvé.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
      <APFormDialog open={edit.open} ap={edit.ap} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} onSaved={load} />
      <ConfirmDelete open={!!del} onOpenChange={(o) => !o && setDel(null)} onConfirm={remove} title="Supprimer cet AP ?" description={del ? `${fullName(del)} (CNAP ${del.cnap}) sera retiré du registre.` : ""} />
    </div>
  );
}
